import { oidcConfig } from '../config';

export type TokenResponse = {
  access_token: string;
  token_type: string;
  expires_in: number;
  id_token?: string;
  refresh_token?: string;
  scope?: string;
};

const STORAGE = {
  accessToken: 'access_token',
  refreshToken: 'refresh_token',
  idToken: 'id_token',
  expiresAt: 'expires_at',
  codeVerifier: 'pkce_code_verifier',
  state: 'oauth_state',
  nonce: 'oauth_nonce',
  returnTo: 'return_to',
} as const;

function redirectUri(): string {
  return `${window.location.origin}/callback`;
}

function base64UrlEncode(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

function randomString(len = 64): string {
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function sha256Base64Url(str: string): Promise<string> {
  const data = new TextEncoder().encode(str);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

// Refresh the access token this many milliseconds before it actually expires,
// so in-flight requests never use an already-expired token.
const EXPIRY_SKEW_MS = 60_000;

export function getAccessToken(): string | null {
  return sessionStorage.getItem(STORAGE.accessToken);
}

function getRefreshToken(): string | null {
  return sessionStorage.getItem(STORAGE.refreshToken);
}

function getExpiresAt(): number {
  return Number(sessionStorage.getItem(STORAGE.expiresAt) ?? '0');
}

function persistTokens(tokenResp: TokenResponse): void {
  sessionStorage.setItem(STORAGE.accessToken, tokenResp.access_token);
  // Refresh tokens are rotated by the IdP: always store the latest one.
  if (tokenResp.refresh_token) {
    sessionStorage.setItem(STORAGE.refreshToken, tokenResp.refresh_token);
  }
  if (tokenResp.id_token) {
    sessionStorage.setItem(STORAGE.idToken, tokenResp.id_token);
  }
  sessionStorage.setItem(
    STORAGE.expiresAt,
    String(Date.now() + tokenResp.expires_in * 1000),
  );
}

export function isLoggedIn(): boolean {
  const access = sessionStorage.getItem(STORAGE.accessToken);
  return Boolean(access) && Date.now() < getExpiresAt();
}

// A session exists if the access token is still valid OR a refresh token is
// available to silently obtain a new one.
export function hasSession(): boolean {
  return isLoggedIn() || Boolean(getRefreshToken());
}

export function clearSession(): void {
  Object.values(STORAGE).forEach((key) => sessionStorage.removeItem(key));
}

export async function login(): Promise<void> {
  const returnTo = window.location.pathname || '/';
  const codeVerifier = randomString(64);
  const codeChallenge = await sha256Base64Url(codeVerifier);
  const state = randomString(24);
  const nonce = randomString(24);

  sessionStorage.setItem(STORAGE.codeVerifier, codeVerifier);
  sessionStorage.setItem(STORAGE.state, state);
  sessionStorage.setItem(STORAGE.nonce, nonce);
  sessionStorage.setItem(STORAGE.returnTo, returnTo);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: oidcConfig.clientId,
    redirect_uri: redirectUri(),
    scope: oidcConfig.scope,
    state,
    nonce,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });

  window.location.href = `${oidcConfig.authorizationEndpoint}?${params.toString()}`;
}

export async function handleCallback(currentUrl: string): Promise<string> {
  const url = new URL(currentUrl);
  const code = url.searchParams.get('code');
  const returnedState = url.searchParams.get('state');
  const error = url.searchParams.get('error');

  if (error) throw new Error(`OAuth error: ${error}`);
  if (!code) throw new Error('No authorization code in callback.');

  const expectedState = sessionStorage.getItem(STORAGE.state);
  if (!expectedState || expectedState !== returnedState) {
    throw new Error('Invalid state.');
  }

  const codeVerifier = sessionStorage.getItem(STORAGE.codeVerifier);
  if (!codeVerifier) throw new Error('Missing code_verifier.');

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: oidcConfig.clientId,
    redirect_uri: redirectUri(),
    code,
    code_verifier: codeVerifier,
  });

  const resp = await fetch(oidcConfig.tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  if (!resp.ok) {
    const txt = await resp.text();
    throw new Error(`Token exchange failed: ${resp.status} ${txt}`);
  }

  const tokenResp = (await resp.json()) as TokenResponse;

  persistTokens(tokenResp);

  const returnTo = sessionStorage.getItem(STORAGE.returnTo) ?? '/';

  sessionStorage.removeItem(STORAGE.codeVerifier);
  sessionStorage.removeItem(STORAGE.state);
  sessionStorage.removeItem(STORAGE.nonce);
  sessionStorage.removeItem(STORAGE.returnTo);

  return returnTo;
}

// De-duplicates concurrent refresh attempts: many parallel API calls share a
// single in-flight refresh request instead of each triggering their own.
let refreshInFlight: Promise<string | null> | null = null;

async function doRefresh(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: oidcConfig.clientId,
    refresh_token: refreshToken,
  });

  try {
    const resp = await fetch(oidcConfig.tokenEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });

    if (!resp.ok) {
      clearSession();
      return null;
    }

    const tokenResp = (await resp.json()) as TokenResponse;
    persistTokens(tokenResp);
    return tokenResp.access_token;
  } catch {
    clearSession();
    return null;
  }
}

/**
 * Returns a valid access token, refreshing it first when it is missing or about
 * to expire. Returns null when no valid token can be obtained (the caller should
 * then redirect to the login page).
 */
export async function getValidAccessToken(): Promise<string | null> {
  const access = getAccessToken();
  if (access && Date.now() < getExpiresAt() - EXPIRY_SKEW_MS) {
    return access;
  }

  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

export function logout(): void {
  clearSession();
  if (oidcConfig.logoutEndpoint) {
    window.location.href = oidcConfig.logoutEndpoint;
    return;
  }
  window.location.href = '/login';
}
